import { useEffect } from 'react'
import { isAnalyticsEnabled, trackEvent } from '../utils/analytics'

// Module-level so a section is counted once per page load, even if the hook remounts
const viewedSections = new Set<string>()

/**
 * Sends one `section_view` event per section the first time its heading enters
 * the viewport. Watching the heading (rather than a share of the whole section)
 * lets long sections register on small screens.
 */
export function useSectionViews() {
  useEffect(() => {
    if (!isAnalyticsEnabled() || typeof IntersectionObserver === 'undefined') return

    const headingToSection = new Map<Element, string>()
    document.querySelectorAll<HTMLElement>('section[id]').forEach((section) => {
      const heading = section.querySelector('h1, h2')
      if (heading && !viewedSections.has(section.id)) {
        headingToSection.set(heading, section.id)
      }
    })

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const section = headingToSection.get(entry.target)
        observer.unobserve(entry.target)
        if (!section || viewedSections.has(section)) return
        viewedSections.add(section)
        trackEvent('section_view', { section })
      })
    })

    headingToSection.forEach((_, heading) => observer.observe(heading))
    return () => observer.disconnect()
  }, [])
}
